import { Box, BoxProps, Icon, Image } from '@chakra-ui/react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { MdSupportAgent } from 'react-icons/md';

import { useAgents } from '@/store/agents';
import { semanticTokens } from '@/theme/foundations';
import { Notification } from '@/types/notifications';

export const getTypeIcon = (
  type: Notification['type']
): React.ComponentType => {
  switch (type) {
    case 'info':
      return Info;
    case 'success':
      return CheckCircle2;
    case 'error':
      return AlertCircle;
    case 'warning':
      return AlertTriangle;
    case 'agent':
      return MdSupportAgent;
    default:
      return Info;
  }
};

const ICON_SIZE = 16;
const AVATAR_SIZE = 32;

export const getNotificationColorConfig = (type: Notification['type']) => {
  switch (type) {
    case 'info':
      return semanticTokens.colors.toast.blue;
    case 'success':
      return semanticTokens.colors.toast.green;
    case 'error':
      return semanticTokens.colors.toast.red;
    case 'warning':
      return semanticTokens.colors.toast.gold;
    case 'agent':
      return semanticTokens.colors.toast.purple;
    default:
      return semanticTokens.colors.toast.blue;
  }
};

export function NotificationIcon({
  type,
  agentId,
  iconOverride,
  ...props
}: {
  type: Notification['type'];
  agentId?: Notification['agents_id'];
  iconOverride?: string | React.ComponentType;
} & BoxProps) {
  const { data: agents } = useAgents();

  const agent = agents?.find(agent => agent.id === agentId);

  const colorConfig = getNotificationColorConfig(type);

  let finalIcon: string | React.ComponentType = getTypeIcon(type);

  if (agent?.avatar?.url && type === 'agent') {
    finalIcon = agent.avatar.url;
  }
  if (iconOverride) {
    finalIcon = iconOverride;
  }

  if (typeof finalIcon === 'string') {
    return (
      <Image
        src={finalIcon}
        boxSize={`${AVATAR_SIZE}px`}
        minW={`${AVATAR_SIZE}px`}
        flexShrink={0}
        borderRadius='full'
        objectFit='cover'
        alt=''
        fallback={<MdSupportAgent size={ICON_SIZE} />}
        {...props}
      />
    );
  }

  return (
    <Box
      bg={colorConfig.bgHover}
      borderRadius='full'
      p='2'
      display='flex'
      alignItems='center'
      justifyContent='center'
      flexShrink={0}
      {...props}
    >
      <Icon as={finalIcon} height={`${ICON_SIZE}px`} width={`${ICON_SIZE}px`} />
    </Box>
  );
}
